import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RefreshCw, Save, Plus, Trash2, Coins, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline editor for per-currency margin. Saves on Enter or blur, shows an
 * emerald check for ~1s after save, and reverts to the placeholder
 * "(default)" hint when cleared. Lives on the same screen as the rate row
 * so admins never need to open a modal to adjust one currency.
 */
function InlineMarginEditor({ code, margin, fallback, onSaved }) {
  const [val, setVal] = React.useState(margin != null ? String(margin) : "");
  const [busy, setBusy] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  React.useEffect(() => { setVal(margin != null ? String(margin) : ""); }, [margin]);

  const persist = async () => {
    const num = val === "" ? null : Number(val);
    // Nothing to save if unchanged
    const unchanged = (num == null && margin == null) || (num != null && Number(margin) === num);
    if (unchanged) return;
    setBusy(true);
    try {
      if (num == null) {
        await api.delete(`/admin/fx/margin/${code}`);
        toast.success(`${code} margin cleared — using default`);
      } else {
        if (isNaN(num) || num < 0 || num > 50) { toast.error("Margin must be 0–50"); return; }
        await api.post("/admin/fx/margin", { code, margin_pct: num });
        toast.success(`${code} margin set to ${num}%`);
      }
      setSaved(true); setTimeout(() => setSaved(false), 1200);
      if (onSaved) onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="inline-flex items-center gap-1.5">
      <div className="relative">
        <Input
          data-testid={`fx-margin-${code}`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={persist}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          placeholder={`${fallback ?? 0}`}
          type="number" step="0.01" min="0" max="50"
          className={"h-8 w-20 text-xs font-mono text-right " +
            (margin != null ? "border-emerald-300 bg-emerald-50/60 text-emerald-800 font-bold" : "")}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">%</span>
      </div>
      {busy && <RefreshCw className="w-3 h-3 animate-spin text-slate-400" />}
      {saved && <Check className="w-3.5 h-3.5 text-emerald-600" />}
      {margin == null && !busy && !saved && (
        <span className="text-[10px] text-slate-400">default</span>
      )}
    </div>
  );
}

export default function AdminFx() {
  const [status, setStatus] = useState(null);
  const [rates, setRates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);   // {code, rate_to_usd, note}
  const [addCcyOpen, setAddCcyOpen] = useState(false);
  const [newCcyCode, setNewCcyCode] = useState("");

  const load = async () => {
    const [s, r] = await Promise.all([
      api.get("/admin/fx/status"),
      api.get("/admin/fx/rates"),
    ]);
    setStatus(s.data);
    setRates(r.data);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async (patch) => {
    setBusy(true);
    try {
      await api.patch("/admin/fx/settings", patch);
      toast.success("FX settings saved — conversion live across all flight searches");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fx/refresh");
      const c = data?.client || {};
      toast.success(`Live rates refreshed — Frankfurter: ${c.frankfurter_count || 0}, open.er-api: ${c.open_erapi_count || 0}`);
      load();
    } catch (e) { toast.error("Refresh failed"); }
    finally { setBusy(false); }
  };

  const saveOverride = async () => {
    if (!edit?.code || !edit?.rate_to_usd) { toast.error("Code + rate required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/fx/override", {
        code: edit.code.toUpperCase(),
        rate_to_usd: Number(edit.rate_to_usd),
        note: edit.note || "",
      });
      toast.success(`${edit.code.toUpperCase()} override saved`);
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const deleteOverride = async (code) => {
    if (!window.confirm(`Delete manual override for ${code}? Frankfurter rate (if any) will be used instead.`)) return;
    try {
      await api.delete(`/admin/fx/override/${code}`);
      toast.success(`${code} override removed`);
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const saveMargin = async () => { /* replaced by InlineMarginEditor */ };

  const addCurrency = async () => {
    const code = (newCcyCode || "").trim().toUpperCase();
    if (!code || code.length !== 3) { toast.error("Enter a 3-letter ISO code (e.g. AED, JPY)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fx/add-currency", { code });
      toast.success(`${data.code} added — 1 ${data.code} = ${Number(data.rate_to_usd).toFixed(6)} USD (${data.source})`);
      setAddCcyOpen(false);
      setNewCcyCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add currency");
    } finally { setBusy(false); }
  };

  const clearMargin = async (code) => {
    if (!window.confirm(`Clear per-currency margin for ${code}? It will revert to the default margin.`)) return;
    try {
      await api.delete(`/admin/fx/margin/${code}`);
      toast.success(`${code} margin cleared — using default`);
      load();
    } catch (e) { toast.error("Clear failed"); }
  };

  if (!status) return <div className="text-slate-500">Loading FX rates…</div>;
  const s = status.settings;

  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)] flex items-center gap-2">
            <Coins className="w-6 h-6 text-[var(--brand-primary)]" /> FX Rates
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Frankfurter (ECB) + open.er-api live feed with manual overrides. Click <strong>Refresh rates</strong> to pull both sources at once — rates are never auto-pulled.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refresh} disabled={busy} variant="outline" data-testid="fx-refresh-btn" className="rounded-full">
            <RefreshCw className={"w-4 h-4 mr-1 " + (busy ? "animate-spin" : "")} /> Refresh rates
          </Button>
          <Button onClick={() => setAddCcyOpen(true)} data-testid="fx-add-currency-btn"
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="w-4 h-4 mr-1" /> Add currency
          </Button>
          <Button onClick={() => setEdit({ code: "", rate_to_usd: "", note: "" })} data-testid="fx-add-override-btn"
                  variant="outline" className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Add manual override
          </Button>
        </div>
      </div>

      {/* Settings card */}
      <Card className="p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label>Target currency</Label>
            <Input
              data-testid="fx-target-currency"
              maxLength={3}
              value={s.target_currency || "USD"}
              onChange={(e) => setStatus({ ...status, settings: { ...s, target_currency: e.target.value.toUpperCase() } })}
              className="font-mono uppercase"
            />
            <div className="text-[10px] text-slate-400 mt-1">ISO-4217 code, e.g. USD / EUR / GBP</div>
          </div>
          <div>
            <Label>Default margin %</Label>
            <Input
              type="number" step="0.01" min="0" max="50"
              data-testid="fx-margin-pct"
              value={s.margin_default_pct ?? 0}
              onChange={(e) => setStatus({ ...status, settings: { ...s, margin_default_pct: parseFloat(e.target.value) || 0 } })}
            />
            <div className="text-[10px] text-slate-400 mt-1">Used when a currency has no per-currency override</div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              data-testid="fx-enabled"
              checked={!!s.enabled}
              onCheckedChange={(v) => setStatus({ ...status, settings: { ...s, enabled: v } })}
            />
            <span className="text-sm">{s.enabled ? "Conversion active" : "Conversion paused"}</span>
          </div>
          <Button
            data-testid="fx-save-settings"
            disabled={busy}
            onClick={() => saveSettings({
              target_currency: s.target_currency,
              margin_default_pct: s.margin_default_pct,
              enabled: s.enabled,
            })}
            className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"
          >
            <Save className="w-4 h-4 mr-1" /> Save settings
          </Button>
        </div>
        <div className="text-[11px] text-slate-500 mt-4 flex items-center gap-2 flex-wrap">
          <span>Frankfurter: <strong>{status.client.frankfurter_count}</strong> currencies</span>
          <span>·</span>
          <span>open.er-api: <strong>{status.client.open_erapi_count}</strong></span>
          <span>·</span>
          <span>Manual overrides: <strong>{status.client.manual_count}</strong></span>
          <span>·</span>
          <span>Last refresh: <strong>{status.client.fetched_at_iso || "—"}</strong></span>
          <a href="https://frankfurter.dev" target="_blank" rel="noreferrer"
             className="text-[var(--brand-primary)] underline inline-flex items-center gap-1 ml-auto">
            frankfurter.dev <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </Card>

      {/* Rate book */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead>
            <TableHead>1 unit = USD</TableHead>
            <TableHead>Effective rate (incl. margin)</TableHead>
            <TableHead>1 USD = unit</TableHead>
            <TableHead>Effective 1 USD = unit</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Margin %</TableHead>
            <TableHead>Note</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rates.map((r) => {
              const baseRate = r.rate_to_usd != null ? Number(r.rate_to_usd) : null;
              const inverseRate = baseRate && baseRate > 0 ? 1 / baseRate : null;
              // Force USD-on-USD margin to 0 for display — matches backend
              // (`fx_client.margin_for('USD')` returns 0.0 regardless of the
              // configured default).
              const effMargin = r.code === "USD"
                ? 0
                : (r.margin_pct != null ? Number(r.margin_pct) : Number(r.effective_margin_pct || 0));
              // Customer-facing FX must be UNFAVOURABLE in every direction —
              // both effective rates below INFLATE by the same margin (they
              // are NOT reciprocals). Whichever way the fare converts, the
              // customer ends up paying more than mid-market and the
              // platform earns the spread.
              const mmult = 1 + (effMargin || 0) / 100;
              const effRate = baseRate != null ? baseRate * mmult : null;                // unit→USD, INFLATED
              const effInverseRate = inverseRate != null ? inverseRate * mmult : null;   // USD→unit, INFLATED
              const isUsd = r.code === "USD";
              return (
              <TableRow key={r.code} data-testid={`fx-rate-${r.code}`}>
                <TableCell className="font-mono font-bold text-[var(--brand-accent)]">{r.code}</TableCell>
                <TableCell className="font-mono">{baseRate != null ? baseRate.toFixed(6) : "—"}</TableCell>
                <TableCell
                  data-testid={`fx-effective-${r.code}`}
                  className={"font-mono " + (effMargin > 0 ? "text-emerald-700 font-semibold" : "text-slate-600")}
                  title={effMargin > 0 ? `${baseRate?.toFixed(6)} × (1 + ${effMargin}%) — 1 ${r.code} is billed to us as MORE USD, so a supplier fare in ${r.code} lands as a bigger USD figure and platform earns the spread` : "No margin applied"}
                >
                  {effRate != null ? effRate.toFixed(6) : "—"}
                  {!isUsd && effMargin > 0 && (
                    <span className="ml-1 text-[10px] text-emerald-600/70">(+{Number(effMargin).toFixed(2)}%)</span>
                  )}
                </TableCell>
                <TableCell
                  data-testid={`fx-inverse-${r.code}`}
                  className="font-mono text-slate-700"
                  title={baseRate != null ? `1 USD = ${inverseRate?.toFixed(4)} ${r.code} (mid-market)` : ""}
                >
                  {inverseRate != null ? inverseRate.toFixed(4) : "—"}
                </TableCell>
                <TableCell
                  data-testid={`fx-inverse-effective-${r.code}`}
                  className={"font-mono " + (effMargin > 0 ? "text-emerald-700 font-semibold" : "text-slate-600")}
                  title={effInverseRate != null ? `1 USD = ${effInverseRate.toFixed(4)} ${r.code} (with ${effMargin}% margin) — this is what the B2C customer is charged per USD` : ""}
                >
                  {effInverseRate != null ? effInverseRate.toFixed(4) : "—"}
                  {!isUsd && effMargin > 0 && (
                    <span className="ml-1 text-[10px] text-emerald-600/70">(+{Number(effMargin).toFixed(2)}%)</span>
                  )}
                </TableCell>
                <TableCell>
                  {r.source === "manual"
                    ? <Badge className="bg-amber-100 text-amber-800 border-0">Manual</Badge>
                    : r.source === "frankfurter"
                    ? <Badge className="bg-teal-50 text-teal-700 border-0">Frankfurter</Badge>
                    : <Badge className="bg-sky-50 text-sky-700 border-0">open.er-api</Badge>}
                </TableCell>
                <TableCell>
                  <InlineMarginEditor
                    code={r.code}
                    margin={r.margin_pct}
                    fallback={r.effective_margin_pct}
                    onSaved={load}
                  />
                </TableCell>
                <TableCell className="text-xs text-slate-500 max-w-xs truncate">{r.note || "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="ghost" data-testid={`fx-edit-${r.code}`}
                            onClick={() => setEdit({ code: r.code, rate_to_usd: r.rate_to_usd, note: r.note || "" })}>
                      {r.manual ? "Edit" : "Override"}
                    </Button>
                    {r.margin_pct != null && (
                      <Button size="sm" variant="ghost" data-testid={`fx-margin-clear-${r.code}`}
                              onClick={() => clearMargin(r.code)} className="text-amber-600 hover:bg-amber-50" title="Clear margin (use default)">
                        ↺
                      </Button>
                    )}
                    {r.manual && (
                      <Button size="sm" variant="ghost" data-testid={`fx-delete-${r.code}`}
                              onClick={() => deleteOverride(r.code)}
                              className="text-rose-600 hover:bg-rose-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
            {rates.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-10">
                No live rates loaded yet — click <strong className="text-[var(--brand-accent)]">Refresh rates</strong> to fetch them.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add Currency dialog — pulls live rate from Frankfurter / open.er-api */}
      <Dialog open={addCcyOpen} onOpenChange={(o) => { if (!o) { setAddCcyOpen(false); setNewCcyCode(""); } }}>
        <DialogContent data-testid="fx-add-currency-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--brand-accent)]">Add a currency</DialogTitle>
            <DialogDescription>
              Pick or type any ISO-4217 code. We&apos;ll pull the live rate from <strong>Frankfurter</strong> first, then <strong>open.er-api</strong> as a fallback. If neither carries the code, use <em>Add manual override</em> instead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>ISO code</Label>
              <Input
                data-testid="fx-add-currency-code"
                list="iso-currency-list"
                maxLength={3}
                value={newCcyCode}
                onChange={(e) => setNewCcyCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter" && newCcyCode.length === 3) addCurrency(); }}
                className="font-mono uppercase"
                placeholder="AED, JPY, INR, BRL…"
                autoFocus
              />
              <datalist id="iso-currency-list">
                {["AED","AFN","ALL","AMD","ANG","AOA","ARS","AUD","AWG","AZN","BAM","BBD","BDT","BGN","BHD","BIF","BMD","BND","BOB","BRL","BSD","BTN","BWP","BYN","BZD","CAD","CDF","CHF","CLP","CNY","COP","CRC","CUP","CVE","CZK","DJF","DKK","DOP","DZD","EGP","ERN","ETB","EUR","FJD","FKP","FOK","GBP","GEL","GGP","GHS","GIP","GMD","GNF","GTQ","GYD","HKD","HNL","HRK","HTG","HUF","IDR","ILS","IMP","INR","IQD","IRR","ISK","JEP","JMD","JOD","JPY","KES","KGS","KHR","KID","KMF","KRW","KWD","KYD","KZT","LAK","LBP","LKR","LRD","LSL","LYD","MAD","MDL","MGA","MKD","MMK","MNT","MOP","MRU","MUR","MVR","MWK","MXN","MYR","MZN","NAD","NGN","NIO","NOK","NPR","NZD","OMR","PAB","PEN","PGK","PHP","PKR","PLN","PYG","QAR","RON","RSD","RUB","RWF","SAR","SBD","SCR","SDG","SEK","SGD","SHP","SLE","SLL","SOS","SRD","SSP","STN","SYP","SZL","THB","TJS","TMT","TND","TOP","TRY","TTD","TVD","TWD","TZS","UAH","UGX","UYU","UZS","VES","VND","VUV","WST","XAF","XCD","XDR","XOF","XPF","YER","ZAR","ZMW","ZWL"].map(c => <option key={c} value={c} />)}
              </datalist>
              <div className="text-[10px] text-slate-400 mt-1">Start typing — autocomplete shows ~160 ISO codes. Rate is pulled the moment you confirm.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAddCcyOpen(false); setNewCcyCode(""); }}>Cancel</Button>
            <Button data-testid="fx-add-currency-save" onClick={addCurrency} disabled={busy || newCcyCode.length !== 3}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus className="w-4 h-4 mr-1" /> Add & fetch rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit override dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        {edit && (
          <DialogContent data-testid="fx-override-dialog">
            <DialogHeader>
              <DialogTitle className="text-[var(--brand-accent)]">Manual rate override — {edit.code || "new currency"}</DialogTitle>
              <DialogDescription>
                Use this for currencies Frankfurter doesn&apos;t ship (AED, EGP, SAR, KWD, QAR, OMR, BHD) or to force a custom rate. Set <strong>1 unit of the source currency in USD</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>ISO code</Label>
                <Input
                  data-testid="fx-override-code"
                  maxLength={3}
                  value={edit.code}
                  onChange={(e) => setEdit({ ...edit, code: e.target.value.toUpperCase() })}
                  className="font-mono uppercase"
                  placeholder="AED"
                />
              </div>
              <div>
                <Label>Rate to USD</Label>
                <Input
                  data-testid="fx-override-rate"
                  type="number" step="0.0001" min="0"
                  value={edit.rate_to_usd}
                  onChange={(e) => setEdit({ ...edit, rate_to_usd: e.target.value })}
                  placeholder="0.2723"
                />
                <div className="text-[10px] text-slate-400 mt-1">e.g. 0.2723 means 1 AED = 0.2723 USD</div>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input
                  data-testid="fx-override-note"
                  value={edit.note || ""}
                  onChange={(e) => setEdit({ ...edit, note: e.target.value })}
                  placeholder="Pegged to USD · Reviewed Q2 2026"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button data-testid="fx-override-save" onClick={saveOverride} disabled={busy}
                      className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
                <Save className="w-4 h-4 mr-1" /> Save override
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
